import * as Sentry from '@sentry/react';
import type { Primitive } from '@sentry/types';
import { Form, Formik } from 'formik';
import { useState, type ReactNode } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Modal } from '@hivebrite/design-system/Dialog';
import { TextAreaInput } from '@hivebrite/design-system/Inputs';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';
import { toast } from '@hivebrite/design-system/Toast';

const GenericErrorBanner: Sentry.FallbackRender = ({ resetError, eventId }) => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  return (
    <Box
      maxWidth="920px"
      margin="auto"
      justifyContent="center"
      flexDirection="column"
      py={4}
    >
      <SimpleCard
        cardProps={{ width: '100%' }}
        title={s__(
          'ErrorBoundary|Sorry, something went wrong. Our team has been notified.',
        )}
        faIconProps={{
          iconName: 'user-robot-xmarks',
        }}
        description={
          <Box mt={4} flexDirection="column" font="h4-regular">
            {s__('ErrorBoundary|Here is what you can do:')}

            <Box pt={4} justifyContent="space-between">
              <Button
                onClick={() => {
                  resetError();
                }}
                mr={1}
              >
                {s__('ErrorBoundary|Reload the page')}
              </Button>

              <Button
                ml={1}
                variant="secondary"
                onClick={() => {
                  setIsFeedbackModalOpen(true);
                }}
              >
                {s__('ErrorBoundary|Report feedback')}
              </Button>
            </Box>

            {isFeedbackModalOpen && (
              <Modal
                header={
                  <Box flexDirection="column">
                    <Box font="h4-semibold" mb={3}>
                      {s__('ErrorBoundary|Send Report')}
                    </Box>
                    <Box textAlign="left">
                      {s__(
                        'ErrorBoundary|Our team has been notified of this technical issue.',
                      )}
                    </Box>
                    <Box textAlign="left">
                      {s__(
                        'ErrorBoundary|Please be aware that this is a one-way crash report, and we will not be able to respond directly.',
                      )}
                    </Box>
                  </Box>
                }
                isOpen={isFeedbackModalOpen}
                onDismiss={() => setIsFeedbackModalOpen(false)}
              >
                <Formik
                  initialValues={{
                    message: '',
                  }}
                  onSubmit={async ({ message }) => {
                    await Sentry.sendFeedback({
                      message,
                      associatedEventId: eventId,
                    });

                    toast.success(
                      s__(
                        'ErrorBoundary|Thank you for your feedback; it has been sent successfully.',
                      ),
                    );

                    resetError();
                  }}
                >
                  {({ setFieldValue }) => (
                    <Form>
                      <TextAreaInput
                        id="error-feedback"
                        name="message"
                        label={s__('ErrorBoundary|Problem description')}
                        placeholder={s__(
                          'ErrorBoundary|Please describe what you were doing on our platform when the issue occurred.',
                        )}
                        required
                        onChange={(event) => {
                          setFieldValue('message', event.target.value);
                        }}
                      />
                      <Box
                        flexDirection="row"
                        gridColumnGap={3}
                        justifyContent="flex-end"
                        mt={3}
                      >
                        <Button
                          mt={4}
                          variant="secondary"
                          onClick={() => setIsFeedbackModalOpen(false)}
                        >
                          {__('Cancel')}
                        </Button>
                        <Button mt={4} variant="primary" type="submit">
                          {s__('ErrorBoundary|Send report')}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </Modal>
            )}
          </Box>
        }
      />
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: ReactNode;
  context?: {
    tags?: { [key: string]: Primitive };
  };
  tribe?:
    | 'builder-tribe'
    | 'evolution-tribe'
    | 'user-journey-tribe'
    | 'admin-xp-squad';
  app?: 'fo' | 'bo' | 'po';
  module?: string;
  shouldReloadPageWhenResettingError?: boolean;
  submodule?: string;
}

const ErrorBoundary = ({
  children,
  context,
  tribe,
  app,
  module,
  shouldReloadPageWhenResettingError = false,
  submodule,
}: ErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      if (context?.tags) {
        scope.setTags(context.tags);
      }

      if (tribe) {
        scope.setTag('tribe', tribe);
      }

      if (app) {
        scope.setTag('app', app);
      }

      if (module) {
        scope.setTag('module', module);
      }

      if (submodule) {
        scope.setTag('submodule', submodule);
      }

      scope.setTag('page_locale', I18n.locale);
    }}
    fallback={(props) => (
      <GenericErrorBanner
        {...props}
        resetError={() => {
          if (shouldReloadPageWhenResettingError) {
            window.location.reload();
          } else {
            props.resetError();
          }
        }}
      />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;

type TribeErrorBoundary = Omit<ErrorBoundaryProps, 'tribe'>;

export const EvErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="evolution-tribe" />
);

export const UjErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="user-journey-tribe" />
);

export const BuilderErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="builder-tribe" />
);

export const AdminXpErrorBoundary = (props: TribeErrorBoundary) => (
  <ErrorBoundary {...props} tribe="admin-xp-squad" />
);
